import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ImageModal from '@/components/common/ImageModal';
import type { LobbyCharacterInfo, PartyMemberInfo } from '@danda/shared';
import { CharacterTypeEnum, ActiveNPCEnum } from '@danda/shared';
import { getSpecialNpcEmoji } from '@danda/shared/utils/avatar-emoji';
import { getStaffAvatarUrl } from '@/lib/media';
import type { Character } from '@/lib/stores/lobbyStore.types';
import { resolveParticipantAvatarUrl } from './resolve-participant-avatar';

interface PartyParticipantsProps {
  /** Party members with name/emoji info from backend */
  members?: PartyMemberInfo[];
  /** Character IDs in party (fallback if members not populated) */
  participants: string[];
  lobbyCharacters: LobbyCharacterInfo[];
  characters: Character[];
  selectedCharacterId: string | null;
  isTavianPresent?: boolean;
}

export function PartyParticipants({
  members,
  participants,
  lobbyCharacters,
  characters,
  selectedCharacterId,
  isTavianPresent,
}: PartyParticipantsProps) {
  const [portraitOverlay, setPortraitOverlay] = useState<{ url: string; name: string } | null>(
    null
  );

  // Use members array from backend if available (has names), else fall back to participants lookup
  const participantInfo = members?.length
    ? members.map((member) => {
        // NPCs are always online, PCs are online if their player is in the lobby
        const lobbyChar = lobbyCharacters.find((c) => c.id === member.id);
        const ownChar = characters.find((c) => c.id === member.id);
        const isNpc = member.type === CharacterTypeEnum.NPC;
        // PC is online if found in lobbyCharacters (player is connected) or is the current user's selected char
        const isOnline = isNpc || !!lobbyChar || member.id === selectedCharacterId;
        const isSelf = member.id === selectedCharacterId;
        const avatarUrl = resolveParticipantAvatarUrl(member, lobbyChar, ownChar, isSelf);
        const portraitUrl =
          member.portraitUrl ||
          lobbyChar?.portraitUrl ||
          ownChar?.portraitUrl ||
          (ownChar?.permanent?.portrait_url as string | undefined) ||
          (ownChar?.permanent?.portraitUrl as string | undefined);
        return {
          id: member.id,
          name: member.name,
          avatar: avatarUrl,
          avatarUrl,
          avatarEmoji: member.avatarEmoji,
          portraitUrl,
          isOnline,
        };
      })
    : participants
        .map((charId) => {
          const lobbyChar = lobbyCharacters.find((c) => c.id === charId);
          const ownChar = characters.find((c) => c.id === charId);
          const name = lobbyChar?.name || ownChar?.name;
          if (!name) return null;

          const isNpc =
            charId === ActiveNPCEnum.TAVIAN ||
            lobbyChar?.type === CharacterTypeEnum.NPC ||
            lobbyChar?.type === 'special_npc';
          const isOnline =
            isNpc || lobbyChar?.type === CharacterTypeEnum.PC || charId === selectedCharacterId;

          const avatarUrl =
            lobbyChar?.avatarUrl ||
            ownChar?.avatarUrl ||
            ownChar?.avatar ||
            (ownChar?.permanent?.avatar_url as string | undefined) ||
            (ownChar?.permanent?.avatarUrl as string | undefined) ||
            lobbyChar?.avatar;

          const portraitUrl =
            lobbyChar?.portraitUrl ||
            ownChar?.portraitUrl ||
            (ownChar?.permanent?.portrait_url as string | undefined) ||
            (ownChar?.permanent?.portraitUrl as string | undefined);

          return {
            id: charId,
            name,
            avatar: avatarUrl,
            avatarUrl,
            avatarEmoji:
              lobbyChar?.avatarEmoji || ownChar?.avatarEmoji || ownChar?.permanent?.avatar_emoji,
            portraitUrl,
            isOnline,
          };
        })
        .filter((p): p is NonNullable<typeof p> => p !== null);

  // If Tavian is present in party chat but not in the list, append him with avatar
  const tavianPresent =
    isTavianPresent && !participantInfo.some((p) => p.id === ActiveNPCEnum.TAVIAN);
  const allParticipants = tavianPresent
    ? [
        {
          id: 'tavian',
          name: 'Tavian',
          avatar: getStaffAvatarUrl('tavian-avatar.webp'),
          avatarUrl: getStaffAvatarUrl('tavian-avatar.webp'),
          avatarEmoji: getSpecialNpcEmoji('tavian'),
          portraitUrl: undefined as string | undefined,
          isOnline: true,
        },
        ...participantInfo,
      ]
    : participantInfo;

  return (
    <div className="flex items-center gap-2 mb-2 pb-2 border-b">
      <div className="flex items-center gap-2 flex-wrap">
        {allParticipants.map((participant) => {
          return (
            <div
              key={participant.id}
              className={`flex items-center gap-1 ${!participant.isOnline ? 'opacity-40' : ''}`}
              title={participant.isOnline ? participant.name : `${participant.name} (offline)`}
            >
              <Avatar
                className={`h-6 w-6 ${!participant.isOnline ? 'grayscale' : ''} ${
                  participant.portraitUrl ? 'cursor-pointer' : ''
                }`}
                onClick={
                  participant.portraitUrl
                    ? () =>
                        setPortraitOverlay({
                          url: participant.portraitUrl!,
                          name: participant.name,
                        })
                    : undefined
                }
              >
                <AvatarImage
                  src={participant.avatarUrl || participant.avatar}
                  alt={participant.name}
                />
                <AvatarFallback className="text-xs">
                  {participant.avatarEmoji || participant.name[0]?.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-muted-foreground font-character">
                {participant.name}
              </span>
            </div>
          );
        })}
      </div>
      <ImageModal
        isOpen={!!portraitOverlay}
        onClose={() => setPortraitOverlay(null)}
        imageUrl={portraitOverlay?.url}
        altText={portraitOverlay?.name ?? ''}
      />
    </div>
  );
}
